import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'

import type { ExtensionInstance } from '@mangovoice/types'

import type {
  ApiError,
  PaginatedApiResult,
  PbxSettingsApi,
} from '../api/pbxSettingsApi.types'
import type { PbxSettingsTranslateFn } from '../types'
import { text } from '../types'
import type {
  PbxSystemSettingsV2,
  PbxV2,
  PhoneNumberOption,
  ProviderGatewayV2,
  SelectOption,
  ServerGroupV2,
} from '../types/pbxSettings.types'
import {
  BRITISH_COLUMBIA_TIME_ZONE,
  GC_TIME_LONG,
  LEGACY_TIMEZONE_OPTIONS,
  QUERY_LIMIT,
  STALE_TIME_LONG,
  STALE_TIME_SHORT,
} from '../utils/pbxSettings.constants'
import {
  mapPaginatedResults,
  normalizePhoneForCompare,
} from '../utils/pbxSettings.utils'

const gatewayLabel = (gateway: ProviderGatewayV2): string =>
  gateway.name?.trim() || gateway.gateway_name?.trim() || String(gateway.id)

const toGatewayOptions = (
  rows: ProviderGatewayV2[] | undefined,
  predicate: (row: ProviderGatewayV2) => boolean,
  currentValue: number | null | undefined,
): SelectOption[] => {
  const currentOptionValue =
    currentValue === null || currentValue === undefined
      ? ''
      : String(currentValue)

  const options = dedupeGatewayRows(
    (rows ?? []).filter(predicate),
    currentOptionValue,
  ).map((row) => ({ value: String(row.id), label: gatewayLabel(row) }))

  if (
    !currentOptionValue ||
    options.some((option) => option.value === currentOptionValue)
  ) {
    return options
  }

  const currentGateway = rows?.find(
    (row) => String(row.id) === currentOptionValue,
  )
  const label = currentGateway
    ? gatewayLabel(currentGateway)
    : currentOptionValue

  return [{ value: currentOptionValue, label, disabled: true }, ...options]
}

const gatewayIdentityKey = (gateway: ProviderGatewayV2): string =>
  (gateway.gateway_name || gateway.name || String(gateway.id))
    .trim()
    .toLowerCase()

const preferGatewayRow = (
  currentValue: string,
  existing: ProviderGatewayV2,
  candidate: ProviderGatewayV2,
): ProviderGatewayV2 => {
  if (String(candidate.id) === currentValue) return candidate
  if (String(existing.id) === currentValue) return existing
  if (candidate.primary_outbound && !existing.primary_outbound) return candidate
  if (existing.primary_outbound && !candidate.primary_outbound) return existing
  if (candidate.primary_international && !existing.primary_international) {
    return candidate
  }
  if (existing.primary_international && !candidate.primary_international) {
    return existing
  }

  const existingPriority = existing.priority ?? Number.MAX_SAFE_INTEGER
  const candidatePriority = candidate.priority ?? Number.MAX_SAFE_INTEGER
  if (candidatePriority !== existingPriority) {
    return candidatePriority < existingPriority ? candidate : existing
  }

  return candidate.id > existing.id ? candidate : existing
}

const dedupeGatewayRows = (
  rows: ProviderGatewayV2[],
  currentValue: string,
): ProviderGatewayV2[] => {
  const rowsByKey = new Map<string, ProviderGatewayV2>()

  for (const row of rows) {
    const key = gatewayIdentityKey(row)
    const existing = rowsByKey.get(key)
    rowsByKey.set(
      key,
      existing ? preferGatewayRow(currentValue, existing, row) : row,
    )
  }

  return Array.from(rowsByKey.values())
}

const mergeProviderGatewayRows = (
  ...rowGroups: Array<ProviderGatewayV2[] | undefined>
): ProviderGatewayV2[] => {
  const rowsById = new Map<number, ProviderGatewayV2>()
  for (const rows of rowGroups) {
    for (const row of rows ?? []) {
      rowsById.set(row.id, row)
    }
  }
  return Array.from(rowsById.values())
}

const currentGatewayIds = (pbx: PbxV2 | undefined): number[] =>
  [
    pbx?.provider_gateway_id,
    pbx?.emergency_provider_gateway_id,
    pbx?.international_provider_gateway_id,
  ].filter((id): id is number => typeof id === 'number' && Number.isFinite(id))

const fetchAllProviderGateways = async (
  api: PbxSettingsApi,
): Promise<ProviderGatewayV2[]> => {
  const results: ProviderGatewayV2[] = []
  let offset = 0

  while (true) {
    const params = offset
      ? { limit: QUERY_LIMIT, offset }
      : { limit: QUERY_LIMIT }
    const { ok, data } = await api.getProviderGatewaysV2(params)
    if (!ok) return Promise.reject(data as ApiError)

    results.push(...mapPaginatedResults<ProviderGatewayV2>(data))

    const page = data as PaginatedApiResult<ProviderGatewayV2>
    if (!page.next || results.length >= page.count) break
    offset += QUERY_LIMIT
  }

  return results
}

const isSelectableOutboundGateway = (gateway: ProviderGatewayV2): boolean =>
  gateway.inbound_outbound === 'outbound' && gateway.primary_outbound

const isSelectableEmergencyGateway = (gateway: ProviderGatewayV2): boolean =>
  gateway.inbound_outbound === '911'

const isSelectableInternationalGateway = (
  gateway: ProviderGatewayV2,
): boolean =>
  gateway.primary_international &&
  (gateway.inbound_outbound === 'outbound' ||
    gateway.inbound_outbound === 'international')

const toLabeledOptions = <T>(
  rows: T[] | undefined,
  map: (row: T) => SelectOption,
): SelectOption[] => (rows ?? []).map(map)

const appendQueryError = (
  errors: string[],
  label: string,
  error: ApiError | null,
  unknownErrorText: string,
) => {
  if (!error) return
  errors.push(`${label}: ${error.message ?? unknownErrorText}`)
}

export const usePbxSettingsQueries = (
  api: PbxSettingsApi,
  pbxIdNum: number | undefined,
  t?: PbxSettingsTranslateFn,
) => {
  const enabled = Boolean(pbxIdNum)
  const safePbxId = pbxIdNum ?? 0

  const pbxQuery = useQuery<PbxV2, ApiError>({
    queryKey: ['pbxV2Edit', pbxIdNum],
    queryFn: async () => {
      const { ok, data } = await api.getPbxV2(safePbxId)
      if (!ok) return Promise.reject(data as ApiError)
      return data as PbxV2
    },
    enabled,
    retry: false,
  })

  const systemSettingsQuery = useQuery<PbxSystemSettingsV2, ApiError>({
    queryKey: ['pbxSystemSettingsV2', pbxIdNum],
    queryFn: async () => {
      const { ok, data } = await api.getSystemSettingsV2({
        pbx_id: safePbxId,
      })
      if (!ok) return Promise.reject(data as ApiError)
      return data as PbxSystemSettingsV2
    },
    enabled,
    retry: false,
  })

  const providerGatewaysQuery = useQuery<ProviderGatewayV2[], ApiError>({
    queryKey: ['providerGatewaysV2', pbxIdNum],
    queryFn: () => fetchAllProviderGateways(api),
    enabled,
    retry: false,
    staleTime: STALE_TIME_SHORT,
  })

  const providerGatewaySeedIds = useMemo(
    () => currentGatewayIds(pbxQuery.data),
    [pbxQuery.data],
  )

  const providerGatewaySeedsQuery = useQuery<ProviderGatewayV2[], ApiError>({
    queryKey: ['providerGatewaySeeds', pbxIdNum, ...providerGatewaySeedIds],
    queryFn: async () => {
      const knownIds = new Set(providerGatewaysQuery.data?.map((row) => row.id))
      const results: ProviderGatewayV2[] = []

      for (const id of providerGatewaySeedIds) {
        if (knownIds.has(id)) continue

        const response = await api.getProviderGatewayV2(id)
        if (response.ok) results.push(response.data as ProviderGatewayV2)
      }

      return results
    },
    enabled:
      enabled &&
      providerGatewaySeedIds.length > 0 &&
      providerGatewaysQuery.isFetched,
    staleTime: STALE_TIME_LONG,
    gcTime: GC_TIME_LONG,
  })

  const providerGatewayRows = useMemo(
    () =>
      mergeProviderGatewayRows(
        providerGatewaysQuery.data,
        providerGatewaySeedsQuery.data,
      ),
    [providerGatewaysQuery.data, providerGatewaySeedsQuery.data],
  )

  const serverGroupsQuery = useQuery<ServerGroupV2[], ApiError>({
    queryKey: ['serverGroupsV2', pbxIdNum],
    queryFn: async () => {
      const { ok, data } = await api.getServerGroupsV2({
        limit: QUERY_LIMIT,
      })
      if (!ok) return Promise.reject(data as ApiError)
      return mapPaginatedResults<ServerGroupV2>(data)
    },
    enabled,
    retry: false,
    staleTime: STALE_TIME_SHORT,
  })

  // Seed values: the specific numbers we need to display immediately.
  const seedCallerIdRaw = systemSettingsQuery.data?.default_caller_id
  const seedFaxRaw = systemSettingsQuery.data?.default_send_fax_number

  const phoneNumberSeedValues = useMemo(() => {
    return [
      ...new Set([seedCallerIdRaw, seedFaxRaw].filter(Boolean).map(String)),
    ]
  }, [seedCallerIdRaw, seedFaxRaw])

  const phoneNumberSeedQuery = useQuery<PhoneNumberOption[], ApiError>({
    queryKey: ['phoneNumberSeeds', pbxIdNum, ...phoneNumberSeedValues],
    queryFn: async () => {
      const results: PhoneNumberOption[] = []
      for (const rawValue of phoneNumberSeedValues) {
        const normalized = normalizePhoneForCompare(rawValue)
        const response = await api.getPhoneNumbers({
          pbx_id: safePbxId,
          number: normalized,
          limit: 1,
        })
        if (response.ok) {
          results.push(...mapPaginatedResults<PhoneNumberOption>(response.data))
        }
      }
      return results
    },
    enabled: enabled && phoneNumberSeedValues.length > 0,
    staleTime: STALE_TIME_LONG,
    gcTime: GC_TIME_LONG,
  })

  const resolvedPhoneNumbers = useMemo(
    () => phoneNumberSeedQuery.data ?? [],
    [phoneNumberSeedQuery.data],
  )

  const virtualFaxMachinesQuery = useQuery<ExtensionInstance[], ApiError>({
    queryKey: ['virtualFaxMachines', pbxIdNum],
    queryFn: async () => {
      const response = await api.getVirtualFaxMachinesWithFallback({
        pbx_id: safePbxId,
      })
      if (!response.ok) {
        return Promise.reject(response.data as ApiError)
      }
      return mapPaginatedResults<ExtensionInstance>(response.data)
    },
    enabled,
    retry: 1,
    staleTime: STALE_TIME_LONG,
    gcTime: GC_TIME_LONG,
  })

  const alert911OptionsQuery = useQuery<ExtensionInstance[], ApiError>({
    queryKey: ['alert911Options', pbxIdNum],
    queryFn: async () => {
      // Legacy parity: legacy edit PBX surfaces broad extension choices here.
      const { ok, data } = await api.getExtensions({
        pbx_id: safePbxId,
        limit: QUERY_LIMIT,
      })
      if (!ok) return Promise.reject(data as ApiError)
      return mapPaginatedResults<ExtensionInstance>(data)
    },
    enabled,
    retry: false,
    staleTime: STALE_TIME_SHORT,
  })

  const outboundGatewayOptions = useMemo(
    () =>
      toGatewayOptions(
        providerGatewayRows,
        isSelectableOutboundGateway,
        pbxQuery.data?.provider_gateway_id,
      ),
    [providerGatewayRows, pbxQuery.data?.provider_gateway_id],
  )

  const emergencyGatewayOptions = useMemo(
    () =>
      toGatewayOptions(
        providerGatewayRows,
        isSelectableEmergencyGateway,
        pbxQuery.data?.emergency_provider_gateway_id,
      ),
    [providerGatewayRows, pbxQuery.data?.emergency_provider_gateway_id],
  )

  const internationalGatewayOptions = useMemo(
    () =>
      toGatewayOptions(
        providerGatewayRows,
        isSelectableInternationalGateway,
        pbxQuery.data?.international_provider_gateway_id,
      ),
    [providerGatewayRows, pbxQuery.data?.international_provider_gateway_id],
  )

  const serverGroupOptions = useMemo<SelectOption[]>(
    () =>
      toLabeledOptions(serverGroupsQuery.data, (sg) => ({
        value: String(sg.id),
        label: sg.name,
      })),
    [serverGroupsQuery.data],
  )

  const virtualFaxOptions = useMemo<SelectOption[]>(
    () =>
      toLabeledOptions(virtualFaxMachinesQuery.data, (fm) => ({
        value: String(fm.id),
        label: `${fm.name} - ${fm.number}`,
      })),
    [virtualFaxMachinesQuery.data],
  )

  const alert911Options = useMemo<SelectOption[]>(
    () =>
      toLabeledOptions(alert911OptionsQuery.data, (ext) => ({
        value: ext.uuid,
        label: `${ext.name} - ${ext.number}`,
      })),
    [alert911OptionsQuery.data],
  )

  const timezoneOptions = useMemo<SelectOption[]>(
    () =>
      LEGACY_TIMEZONE_OPTIONS.flatMap((option) =>
        option.value === 'Pacific Time (US & Canada)'
          ? [
              option,
              {
                value: BRITISH_COLUMBIA_TIME_ZONE,
                label: text(
                  t,
                  'pbxSettings.fields.defaultTimeZone.britishColumbia',
                  'Pacific Time - British Columbia (PCT)',
                ),
              },
            ]
          : [option],
      ),
    [t],
  )

  const secondaryQueryErrors = useMemo(() => {
    const errors: string[] = []
    const unknownErrorText = text(
      t,
      'pbxSettings.partialLoadWarning.unknownError',
      'Unknown error',
    )
    appendQueryError(
      errors,
      text(
        t,
        'pbxSettings.partialLoadWarning.labels.providerGateways',
        'Provider gateways',
      ),
      providerGatewaysQuery.error,
      unknownErrorText,
    )
    appendQueryError(
      errors,
      text(
        t,
        'pbxSettings.partialLoadWarning.labels.serverGroups',
        'Server groups',
      ),
      serverGroupsQuery.error,
      unknownErrorText,
    )
    appendQueryError(
      errors,
      text(
        t,
        'pbxSettings.partialLoadWarning.labels.phoneNumbers',
        'Phone numbers',
      ),
      phoneNumberSeedQuery.error,
      unknownErrorText,
    )
    appendQueryError(
      errors,
      text(
        t,
        'pbxSettings.partialLoadWarning.labels.virtualFaxes',
        'Virtual faxes',
      ),
      virtualFaxMachinesQuery.error,
      unknownErrorText,
    )
    appendQueryError(
      errors,
      text(
        t,
        'pbxSettings.partialLoadWarning.labels.alert911Options',
        '911 options',
      ),
      alert911OptionsQuery.error,
      unknownErrorText,
    )
    return errors
  }, [
    t,
    providerGatewaysQuery.error,
    serverGroupsQuery.error,
    phoneNumberSeedQuery.error,
    virtualFaxMachinesQuery.error,
    alert911OptionsQuery.error,
  ])

  return {
    pbxQuery,
    systemSettingsQuery,
    phoneNumbers: resolvedPhoneNumbers,
    outboundGatewayOptions,
    emergencyGatewayOptions,
    internationalGatewayOptions,
    serverGroupOptions,
    virtualFaxOptions,
    alert911Options,
    timezoneOptions,
    virtualFaxLoading: virtualFaxMachinesQuery.isLoading,
    secondaryQueryErrors,
  }
}
